import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "./config";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../A5StaticData/form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { useTranslation } from "react-i18next";

const filterA5TransactionsSchema = z.object({
  a5_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a5_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a5_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a5_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a5_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  a5_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

const filterA5TransactionsFields = filterA5TransactionsSchema.keyof().enum;

export type FilterA5TransactionsSchema = z.infer<
  typeof filterA5TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterA5TransactionsSchema>[] = [
  {
    name: filterA5TransactionsFields.a5_portfolio_bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterA5TransactionsFields.a5_portfolio_client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterA5TransactionsFields.a5_portfolio_vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterA5TransactionsFields.a5_portfolio_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterA5TransactionsFields.a5_portfolio_account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterA5Transactions() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.A5_TRANSACTIONS;

  const fields: Field[] = [
    {
      name: filterA5TransactionsFields.a5_portfolio_client_contains,
      label: t(`${tableName}.searchbox.a5_portfolio_client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterA5TransactionsSchema>(
        portfolioDepFields,
        filterA5TransactionsFields.a5_portfolio_client_contains,
      ),
    },
    {
      name: filterA5TransactionsFields.a5_portfolio_vehicle_contains,
      label: t(`${tableName}.searchbox.a5_portfolio_vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterA5TransactionsSchema>(
        portfolioDepFields,
        filterA5TransactionsFields.a5_portfolio_vehicle_contains,
      ),
    },
    {
      name: filterA5TransactionsFields.a5_portfolio_account_name_contains,
      label: t(`${tableName}.searchbox.a5_portfolio_account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterA5TransactionsSchema>(
        portfolioDepFields,
        filterA5TransactionsFields.a5_portfolio_account_name_contains,
      ),
    },
    {
      name: filterA5TransactionsFields.a5_portfolio_name_contains,
      label: t(`${tableName}.searchbox.a5_portfolio_name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterA5TransactionsSchema>(
        portfolioDepFields,
        filterA5TransactionsFields.a5_portfolio_name_contains,
      ),
    },
    {
      name: filterA5TransactionsFields.a5_portfolio_bank_name_contains,
      label: t(`${tableName}.searchbox.a5_portfolio_bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterA5TransactionsSchema>(
        portfolioDepFields,
        filterA5TransactionsFields.a5_portfolio_bank_name_contains,
      ),
    },
    {
      name: filterA5TransactionsFields.actif_contains,
      label: t(`${tableName}.searchbox.actif_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_STATIC_DATA}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterA5TransactionsFields.asset_class_1_equals,
      assetSubclassFieldName: filterA5TransactionsFields.asset_class_2_equals,
      assetClassLabel: t(`${tableName}.searchbox.asset_class_1_equals`),
      assetSubclassLabel: t(`${tableName}.searchbox.asset_class_2_equals`),
      client: Clients.A5,
      staticDataTableName: Tables.A5_STATIC_DATA,
      language: "fr",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterA5TransactionsFields.category_contains,
      label: t(`${tableName}.searchbox.category_contains`, { defaultValue: "Transaction type" }),
      type: "sectionSelect",
      useTranslation: true,
      section_name_translation_prefix: "transactions.category_groups",
      translate_key: "category",
      client: Clients.A5,
      table_name: Tables.A5_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_CATEGORY_OPTIONS,
    },
    {
      name: filterA5TransactionsFields.transfer_contains,
      label: t(`${tableName}.searchbox.transfer_contains`, { defaultValue: "Transfer type" }),
      type: "select",
      useTranslation: true,
      client: Clients.A5,
      table_name: Tables.A5_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: filterA5TransactionsFields.fee_type_contains,
      label: t(`${tableName}.searchbox.fee_type_contains`, { defaultValue: "Fee type" }),
      type: "select",
      useTranslation: true,
      client: Clients.A5,
      table_name: Tables.A5_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: filterA5TransactionsFields.description_contains,
      label: t(`${tableName}.searchbox.description_contains`, { defaultValue: "Description" }),
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterA5TransactionsFields.date_from },
      to: { name: filterA5TransactionsFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterA5TransactionsFields.create_from },
      to: { name: filterA5TransactionsFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.A5}
      schema={filterA5TransactionsSchema}
      tableName={Tables.A5_TRANSACTIONS}
      fields={fields}
    />
  );
}
